import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bookingQuery, isCancelEligible, isCheckoutEditable, type BookingDetail } from '@/lib/bookings';
import { apartmentsQuery, type Apartment } from '@/lib/apartments';
import { cancelBooking, cancelErrorToast, cancelSuccessMessage } from '@/lib/cancel';
import { usePermissions } from '@/lib/me';
import { posthog } from '@/lib/posthog';
import { formatMoney } from '@/lib/format';
import { BookingStatusBadge } from '@/components/StatusBadges';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import { useConfirm } from '@/components/DialogProvider';
import { usePageHeader } from '@/components/PageHeader';
import { useToast } from '@/components/ToastProvider';
import { GuestCard, BookingCard, MetaCard } from '@/components/booking/cards';
import { BookingAutomationDebug } from '@/components/BookingAutomationDebug';
import { ChangeCheckoutPanel } from '@/components/booking/ChangeCheckoutPanel';
import { PaymentsCard } from '@/components/booking/PaymentsCard';
import { RevisionsTimeline } from '@/components/booking/RevisionsTimeline';
import { ServiceInfoEditor } from '@/components/booking/ServiceInfoEditor';
import { RefundsCard } from '@/components/finance/RefundsCard';
import { StayExpensesCard } from '@/components/finance/StayExpensesCard';
import { financeCategoriesQuery } from '@/lib/finance';

export const Route = createFileRoute('/_app/bookings/$id')({
  // Pre-fetch both the booking and apartments map. Catch 401/404 here so the
  // component owns the user-facing error UI (richer than the app-wide
  // defaultErrorComponent, which would otherwise catch the throw).
  loader: async ({ context, params }) => {
    const apartmentsPromise = context.queryClient
      .ensureQueryData(apartmentsQuery)
      .catch(() => undefined);
    try {
      await context.queryClient.ensureQueryData(bookingQuery(params.id));
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 401 || status === 404) {
        await apartmentsPromise;
        return;
      }
      throw err;
    }
    await apartmentsPromise;
  },
  component: BookingDetailPage,
});

// ─── Tab definitions ──────────────────────────────────────────────────────────

type BookingDetailTab = 'overview' | 'service' | 'payments' | 'timeline' | 'debug';

const TABS: TabItem[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'service', label: 'Service & requests' },
  { value: 'payments', label: 'Payments' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'debug', label: 'Debug' },
];

// ─── Main page ────────────────────────────────────────────────────────────────

export function BookingDetailPage() {
  const { id } = Route.useParams();

  const {
    data: booking,
    error,
    isLoading,
  } = useQuery({ ...bookingQuery(id), throwOnError: false });

  const { data: apartments } = useQuery(apartmentsQuery);
  const aptMap = new Map<string, Apartment>(
    (apartments ?? []).map((a) => [a.id, a]),
  );

  // Gate affordances on effective permissions (roles + direct grants), the
  // same actions the backend authorizes — not on a hand-rolled role list.
  const { can } = usePermissions();
  const userCanCancel = can('bookings.cancel');
  const userCanWrite = can('bookings.write');
  const canReadFinance = can('finance.read');
  const canConfirmFinance = can('finance.confirm');
  // enabled: gated the same way the query itself would 403 without
  // finance.read — a listings_manager (bookings.write, no finance.*)
  // viewing this page must never issue this request at all, not just
  // fail to render its result. include_inactive=true: a stay expense
  // classified under an article since deactivated must still show its
  // real name here, not fall back to a bare id (matches
  // finance.movements.$id.tsx's own categories.data lookup).
  const categories = useQuery({ ...financeCategoriesQuery(true), enabled: canReadFinance });
  const queryClient = useQueryClient();
  const askConfirm = useConfirm();
  const { toast } = useToast();
  const [cancelPending, setCancelPending] = useState(false);
  const [tab, setTab] = useState<BookingDetailTab>('overview');
  const [changeCheckoutOpen, setChangeCheckoutOpen] = useState(false);

  // Title lives in the Topbar (navTitle → "Bookings"); publish the short id as
  // the subtitle. Called before the early-return branches for stable hook
  // order; undefined while loading → Topbar falls back to navSubtitle.
  usePageHeader({ subtitle: booking ? `#${booking.id.slice(0, 8)}` : undefined });

  // Error handling ─────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="max-w-3xl">
        <BackLink />
        <p className="mt-6 text-sm text-muted">Loading…</p>
      </div>
    );
  }

  const errStatus = (error as { status?: number } | null)?.status;

  if (errStatus === 404) {
    return (
      <div className="max-w-3xl">
        <BackLink />
        <div className="mt-8 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card px-6 py-10 text-center">
          <p className="text-lg font-semibold">Booking not found</p>
          <p className="mt-1 text-sm text-muted">
            The booking <code className="font-mono">{id}</code> does not exist or was deleted.
          </p>
          <Link
            to="/bookings"
            className="mt-4 inline-block text-sm text-primary underline hover:no-underline"
          >
            Back to bookings list
          </Link>
        </div>
      </div>
    );
  }

  if (errStatus === 401) {
    return (
      <div className="max-w-3xl">
        <BackLink />
        <div className="mt-6 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive">
          Session expired.{' '}
          <Link to="/login" className="font-medium underline">
            Sign in again
          </Link>
          .
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl">
        <BackLink />
        <div
          role="alert"
          className="mt-6 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          Failed to load booking: {String(error)}
        </div>
      </div>
    );
  }

  if (!booking) return null;

  const aptTz = aptMap.get(booking.apartment_id)?.timezone ?? 'UTC';

  const bk = booking;

  // Deterministic cancel for a booking WE own (ARI-only direct/manual) — flip +
  // recompute availability + conditional refund (ADR-034). OTA/Channex bookings
  // are not eligible (they cancel in Channex); the control is also finance-gated.
  async function onCancelBooking() {
    const apt = aptMap.get(bk.apartment_id);
    const aptLabel = apt ? apt.title : bk.apartment_id;
    const ok = await askConfirm({
      title: 'Cancel this booking?',
      body:
        `This cancels the booking and releases the nights. If a payment was captured, the guest is refunded.\n\n` +
        `${aptLabel}\n` +
        `${bk.checkin_date} → ${bk.checkout_date}\n` +
        `${formatMoney(bk.total_amount_minor, bk.currency)}` +
        (bk.guest_email ? `\n${bk.guest_email}` : '') +
        `\n\nThis cannot be undone.`,
      confirmLabel: 'Cancel booking',
      cancelLabel: 'Keep',
      destructive: true,
    });
    if (!ok) return;

    setCancelPending(true);
    try {
      const res = await cancelBooking(bk.id);
      posthog.capture?.('pms_action', { action: 'booking_cancelled' });
      toast({
        kind: 'success',
        title: 'Booking cancelled',
        message: cancelSuccessMessage(res),
      });
      await queryClient.invalidateQueries({ queryKey: bookingQuery(bk.id).queryKey });
      await queryClient.invalidateQueries({ queryKey: ['bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['ari', 'calendar'] });
    } catch (err) {
      toast(cancelErrorToast(err));
    } finally {
      setCancelPending(false);
    }
  }

  const cancelEligible = isCancelEligible(bk) && userCanCancel;
  const checkoutEditable = isCheckoutEditable(bk) && userCanWrite;

  // Last revision acked check ──────────────────────────────────────────────────
  const lastRevision = booking.revisions[0] ?? null; // newest first
  const hasUnackedRevision = lastRevision !== null && !lastRevision.acked_at;

  return (
    <div className="max-w-3xl space-y-6">
      {/* Back link */}
      <BackLink />

      {/* Header: "Booking #id" lives in the Topbar; this row carries status */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <BookingStatusBadge status={booking.status} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {checkoutEditable && (
            <Button variant="secondary" size="sm" onClick={() => setChangeCheckoutOpen(true)}>
              Change checkout
            </Button>
          )}
          {cancelEligible && (
            <Button variant="destructive" size="sm" loading={cancelPending} onClick={onCancelBooking}>
              Cancel booking
            </Button>
          )}
        </div>
      </div>

      {/* Unacked revision banner */}
      {hasUnackedRevision && (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-[var(--radius-control)] border border-[var(--ollio-warning)] bg-[var(--ollio-warning-bg)] px-4 py-3 text-sm text-[var(--text-body)]"
        >
          <span aria-hidden="true">⚠</span>
          <span>
            This booking has an unacked revision — check channel sync.
          </span>
        </div>
      )}

      {/* Tabs */}
      <Tabs
        variant="pill"
        value={tab}
        onChange={(v) => setTab(v as BookingDetailTab)}
        tabs={TABS}
      />

      <div className="mt-4">
        {tab === 'overview' && (
          <div className="grid gap-4 md:grid-cols-2">
            <GuestCard booking={booking} />
            <BookingCard booking={booking} aptMap={aptMap} />
          </div>
        )}
        {tab === 'service' && (
          userCanWrite
            ? <ServiceInfoEditor bookingId={booking.id} tz={aptTz} serviceInfo={booking.service_info} />
            : <ServiceReadView booking={booking} />
        )}
        {tab === 'payments' && (
          <div className="flex flex-col gap-4">
            <PaymentsCard booking={booking} />
            {canReadFinance && <RefundsCard bookingId={booking.id} canConfirm={canConfirmFinance} />}
            {canReadFinance && (
              <StayExpensesCard
                bookingId={booking.id}
                categoryNames={Object.fromEntries((categories.data ?? []).map((c) => [c.id, c.name]))}
              />
            )}
          </div>
        )}
        {tab === 'timeline' && <RevisionsTimeline revisions={booking.revisions} />}
        {tab === 'debug' && (
          <div className="space-y-4">
            <BookingAutomationDebug bookingId={booking.id} />
            <MetaCard meta={booking.meta} />
          </div>
        )}
      </div>

      <Dialog
        open={changeCheckoutOpen}
        onClose={() => setChangeCheckoutOpen(false)}
        title="Change checkout"
        description="Move the checkout date and set the new total. Past dates are allowed."
        side
      >
        {changeCheckoutOpen && (
          <ChangeCheckoutPanel booking={booking} onClose={() => setChangeCheckoutOpen(false)} />
        )}
      </Dialog>
    </div>
  );
}

// ─── Service read view (read-only summary; Task 5 swaps for the editor) ──────

function ServiceReadView({ booking }: { booking: BookingDetail }) {
  const si = booking.service_info;
  const requests: string[] = [];
  if (si) {
    if (si.early_checkin) requests.push('Early check-in');
    if (si.late_checkout) requests.push('Late check-out');
    if (si.parking_needed) requests.push(`Parking${si.parking_note ? ` (${si.parking_note})` : ''}`);
    if (si.pets) requests.push('Pets');
    if (si.crib > 0) requests.push(`Crib ×${si.crib}`);
    if (si.high_chair > 0) requests.push(`High chair ×${si.high_chair}`);
  }
  const hasContent = !!si?.notes || requests.length > 0;

  if (!hasContent) {
    return <p className="text-sm text-muted">No operational details recorded yet.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {requests.length > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            Service requests
          </h3>
          <ul className="flex flex-wrap gap-1.5">
            {requests.map((r) => (
              <li
                key={r}
                className="rounded-full border border-[var(--border-hairline)] bg-[var(--surface-sunken)] px-2.5 py-0.5 text-xs"
              >
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
          Internal notes
        </h3>
        {si?.notes ? (
          <p className="whitespace-pre-wrap text-sm text-foreground">{si.notes}</p>
        ) : (
          <p className="text-sm text-muted">No internal notes.</p>
        )}
      </div>
    </div>
  );
}

// ─── Back link ────────────────────────────────────────────────────────────────

function BackLink() {
  return (
    <Link
      to="/bookings"
      className="inline-flex items-center gap-1 text-sm text-muted hover:text-foreground"
    >
      <span aria-hidden="true">←</span> Back to bookings
    </Link>
  );
}
